import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import AxiosInstance from "../../services/networkServices";
import PageLayout from "../../components/PageLayout";
import CommonPagination from "../../components/Table/CommonPagination";
import { showToast } from "../../store/slices/common";

const MODE_OPTIONS = [
  {
    value: "off",
    label: "Off",
    help: "Feature dormant. The app behaves exactly as before.",
  },
  {
    value: "app_wide",
    label: "Required app-wide",
    help:
      "Every user must verify. New signups are blocked until verified; existing accounts get a dismissible prompt during the grace period, then a hard block.",
  },
  {
    value: "live_only",
    label: "Required for live only",
    help:
      "Only going live and participating in live pagents (rap, debate, karaoke live) requires verification. Recorded video/photo matches are unaffected.",
  },
];

const REVERIFY_OPTIONS = [
  { value: "once", label: "Verify once (never expires)" },
  { value: "30", label: "Every 30 days" },
  { value: "90", label: "Every 3 months (90 days)" },
  { value: "180", label: "Every 6 months (180 days)" },
  { value: "365", label: "Every year (365 days)" },
];

const STATUS_FILTERS = [
  { value: "appeal_requested", label: "Appeal requested" },
  { value: "flagged", label: "Needs attention (duplicate / underage / review)" },
  { value: "rejected_duplicate", label: "Duplicate face" },
  { value: "rejected_underage", label: "Under age" },
  { value: "needs_review", label: "Needs manual review" },
  { value: "verified", label: "Verified" },
  { value: "all", label: "All attempted" },
];

const STATUS_CHIP = {
  verified: { label: "Verified", color: "success" },
  rejected_duplicate: { label: "Duplicate", color: "error" },
  rejected_underage: { label: "Under age", color: "error" },
  needs_review: { label: "Needs review", color: "warning" },
  unverified: { label: "Unverified", color: "default" },
};

const PAGE_SIZE = 20;

const cardSx = {
  padding: "20px",
  borderRadius: "10px",
  boxShadow:
    "rgba(0, 0, 0, 0.05) 0 6px 24px 0, rgba(0, 0, 0, 0.08) 0 0 0 1px",
  boxSizing: "border-box",
};

const headCellSx = {
  fontWeight: 700,
  color: "#142C5C",
  backgroundColor: "#f4f6fb",
  fontSize: "13px",
};

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

const selfieThumbSx = {
  width: 56,
  height: 56,
  borderRadius: 8,
  objectFit: "cover",
  display: "block",
  cursor: "pointer",
  border: "1px solid rgba(0,0,0,0.12)",
  backgroundColor: "#f4f6fb",
};

const selfiePlaceholderSx = {
  ...selfieThumbSx,
  cursor: "default",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "text.disabled",
  fontSize: 10,
  textAlign: "center",
  lineHeight: 1.2,
  px: 0.5,
};

/**
 * One selfie thumbnail (or a "no photo" placeholder). Accounts verified
 * before selfies were retained, and transient S3 failures, have no URL.
 */
/** "City, Region, Country" from a GeoIP estimate, or null when nothing resolved. */
const formatPlace = (s) => {
  if (!s) return null;
  const region = s.regionCode ? s.regionCode.split("-").slice(1).join("-") : "";
  const parts = [s.city, region, s.country].filter(Boolean);
  return parts.length ? parts.join(", ") : null;
};

/**
 * Where a selfie was submitted from: GeoIP place + IP. `other` is the matched
 * account's submission (duplicates) so a shared IP can be called out.
 */
const SubmissionOrigin = ({ submission, other, label }) => {
  if (!submission?.ip && !formatPlace(submission)) {
    return (
      <Typography variant="caption" color="text.disabled">
        {label ? `${label}: ` : ""}—
      </Typography>
    );
  }
  const place = formatPlace(submission);
  const sameIp = !!(submission.ip && other?.ip && submission.ip === other.ip);
  const tooltip = [
    submission.accuracyKm != null ? `GeoIP accuracy ±${submission.accuracyKm} km` : null,
    submission.lat != null && submission.lng != null
      ? `${Number(submission.lat).toFixed(3)}, ${Number(submission.lng).toFixed(3)}`
      : null,
    submission.at ? `Submitted ${formatDate(submission.at)}` : null,
    "IP geolocation is an estimate (VPNs / carrier NAT shift it).",
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <Tooltip title={tooltip}>
      <Box sx={{ lineHeight: 1.3 }}>
        {label && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            {label}
          </Typography>
        )}
        <Typography variant="body2" sx={{ fontSize: 13 }}>
          {place || "Unknown location"}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontFamily: "monospace",
            color: sameIp ? "error.main" : "text.secondary",
            fontWeight: sameIp ? 700 : 400,
          }}
        >
          {submission.ip || "no IP"}
          {sameIp ? " · same IP" : ""}
        </Typography>
      </Box>
    </Tooltip>
  );
};

/** Large selfie with title/caption for the compare dialog. */
const SelfiePanel = ({ title, caption, url }) => (
  <Box>
    <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
      {caption}
    </Typography>
    {url ? (
      <Box
        component="img"
        src={url}
        alt={title}
        sx={{
          width: "100%",
          aspectRatio: "3 / 4",
          objectFit: "cover",
          borderRadius: 2,
          border: "1px solid rgba(0,0,0,0.12)",
          backgroundColor: "#f4f6fb",
          display: "block",
        }}
      />
    ) : (
      <Box
        sx={{
          width: "100%",
          aspectRatio: "3 / 4",
          borderRadius: 2,
          border: "1px dashed rgba(0,0,0,0.2)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "text.disabled",
          px: 2,
          textAlign: "center",
        }}
      >
        No selfie on file (verified before selfies were retained, or the photo was reset).
      </Box>
    )}
  </Box>
);

const SelfieThumb = ({ url, label, onClick }) =>
  url ? (
    <Tooltip title={`${label} — click to enlarge`}>
      <Box component="img" src={url} alt={label} sx={selfieThumbSx} onClick={onClick} />
    </Tooltip>
  ) : (
    <Tooltip title={`${label}: no selfie on file`}>
      <Box sx={selfiePlaceholderSx}>no photo</Box>
    </Tooltip>
  );

const toReverifyValue = (days) =>
  days === null || days === undefined ? "once" : String(days);

/**
 * Face Verification admin page: enforcement policy (mode / reverification /
 * grace / retention) + review queue for users Rekognition flagged.
 */
const ManageFaceVerification = () => {
  const dispatch = useDispatch();

  // ---- Settings -----------------------------------------------------------
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("off");
  const [reverify, setReverify] = useState("once");
  const [customReverifyDays, setCustomReverifyDays] = useState("");
  const [gracePeriodDays, setGracePeriodDays] = useState("7");
  const [minAge, setMinAge] = useState("13");
  const [retainFaceOnDelete, setRetainFaceOnDelete] = useState(false);
  const [enabledAt, setEnabledAt] = useState(null);

  const applySettings = useCallback((cfg) => {
    if (!cfg) return;
    setMode(cfg.mode || "off");
    const days = cfg.reverificationDays;
    const preset = REVERIFY_OPTIONS.some((o) => o.value === toReverifyValue(days));
    setReverify(preset ? toReverifyValue(days) : "custom");
    setCustomReverifyDays(preset || days == null ? "" : String(days));
    setGracePeriodDays(String(cfg.gracePeriodDays ?? 7));
    setMinAge(String(cfg.minAge ?? 13));
    setRetainFaceOnDelete(cfg.retainFaceOnDelete === true);
    setEnabledAt(cfg.enabledAt || null);
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await AxiosInstance.get("/admin/settings/face-verification");
      if (response.data.status) {
        applySettings(response.data.data.faceVerification);
      }
    } catch (error) {
      console.error("Error fetching face verification settings:", error);
      dispatch(
        showToast({
          color: "error",
          msg:
            error?.response?.data?.message ||
            "Failed to load face verification settings.",
        })
      );
    } finally {
      setLoading(false);
    }
  }, [applySettings, dispatch]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const buildPayload = () => {
    let reverificationDays = null;
    if (reverify === "custom") {
      const n = Number(customReverifyDays);
      if (!Number.isInteger(n) || n < 1) {
        dispatch(
          showToast({
            color: "error",
            msg: "Custom reverification period must be a whole number of days (1+).",
          })
        );
        return null;
      }
      reverificationDays = n;
    } else if (reverify !== "once") {
      reverificationDays = Number(reverify);
    }

    const grace = Number(gracePeriodDays);
    if (!Number.isInteger(grace) || grace < 0 || grace > 365) {
      dispatch(
        showToast({
          color: "error",
          msg: "Grace period must be between 0 and 365 days.",
        })
      );
      return null;
    }

    const age = Number(minAge);
    if (!Number.isInteger(age) || age < 0 || age > 120) {
      dispatch(showToast({ color: "error", msg: "Minimum age must be 0–120." }));
      return null;
    }

    return {
      mode,
      reverificationDays,
      gracePeriodDays: grace,
      minAge: age,
      retainFaceOnDelete,
    };
  };

  const handleSave = async () => {
    const payload = buildPayload();
    if (!payload) return;
    try {
      setSaving(true);
      const response = await AxiosInstance.put(
        "/admin/settings/face-verification",
        payload
      );
      if (response.data.status) {
        applySettings(response.data.data?.faceVerification);
        dispatch(
          showToast({ color: "success", msg: "Face verification settings saved." })
        );
      } else {
        dispatch(
          showToast({
            color: "error",
            msg: response.data.message || "Failed to save settings.",
          })
        );
      }
    } catch (error) {
      console.error("Error saving face verification settings:", error);
      dispatch(
        showToast({
          color: "error",
          msg: error?.response?.data?.message || "Failed to save settings.",
        })
      );
    } finally {
      setSaving(false);
    }
  };

  // ---- Review queue ---------------------------------------------------------
  const [statusFilter, setStatusFilter] = useState("flagged");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(0);
  }, [statusFilter, debouncedSearch]);

  useEffect(() => {
    let cancelled = false;
    const fetchList = async () => {
      setListLoading(true);
      try {
        const response = await AxiosInstance.post("/admin/face-verification/list", {
          page: page + 1,
          limit: PAGE_SIZE,
          status: statusFilter,
          ...(debouncedSearch && { search: debouncedSearch }),
        });
        if (cancelled) return;
        const data = response.data?.data || {};
        setRows(data.list || []);
        setTotal(data.resultCount ?? (data.list || []).length);
      } catch (error) {
        if (cancelled) return;
        console.error("Error fetching face verification list:", error);
        dispatch(
          showToast({
            color: "error",
            msg: error?.response?.data?.message || "Failed to load review queue.",
          })
        );
      } finally {
        if (!cancelled) setListLoading(false);
      }
    };
    fetchList();
    return () => {
      cancelled = true;
    };
  }, [page, statusFilter, debouncedSearch, refreshToken, dispatch]);

  // ---- Actions ----------------------------------------------------------------
  const [pendingAction, setPendingAction] = useState(null); // { user, action }
  const [actionNote, setActionNote] = useState("");
  const [acting, setActing] = useState(false);

  const openAction = (user, action) => {
    setPendingAction({ user, action });
    setActionNote("");
  };

  const runAction = async () => {
    if (!pendingAction) return;
    const { user, action } = pendingAction;
    try {
      setActing(true);
      const response = await AxiosInstance.post("/admin/face-verification/action", {
        userId: user._id,
        action,
        ...(actionNote.trim() && { note: actionNote.trim() }),
      });
      if (response.data.status) {
        dispatch(
          showToast({
            color: "success",
            msg:
              action === "approve"
                ? `@${user.username} approved.`
                : action === "reset"
                  ? `@${user.username} reset — they can verify again.`
                  : `@${user.username} banned.`,
          })
        );
        setPendingAction(null);
        setRefreshToken((t) => t + 1);
      } else {
        dispatch(
          showToast({
            color: "error",
            msg: response.data.message || "Action failed.",
          })
        );
      }
    } catch (error) {
      console.error("Face verification action failed:", error);
      dispatch(
        showToast({
          color: "error",
          msg: error?.response?.data?.message || "Action failed.",
        })
      );
    } finally {
      setActing(false);
    }
  };

  // ---- Selfie compare dialog ------------------------------------------------
  const [compare, setCompare] = useState(null); // { row }

  const modeHelp = MODE_OPTIONS.find((o) => o.value === mode)?.help;

  return (
    <PageLayout
      title="Face Verification"
      subtitle="One account per person + 13+ age check (AWS Rekognition). The selfie behind each decision is kept privately for this review page only; links below expire after 15 minutes — refresh to renew."
      loading={loading}
      actions={
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving || loading}
        >
          {saving ? "Saving…" : "Save settings"}
        </Button>
      }
    >
      <Grid container spacing={3}>
        {/* ---------------- Policy ---------------- */}
        <Grid item xs={12} md={7}>
          <Paper sx={cardSx}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Enforcement
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Turning this on asks users for a quick selfie. The app explains why
              (one account per person, 13+) and states that Pagent does not store
              facial information for anything beyond anti-cheating and age
              verification.
            </Typography>

            <TextField
              label="Requirement"
              select
              size="small"
              fullWidth
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              helperText={modeHelp}
              sx={{ mb: 2 }}
            >
              {MODE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Reverification"
                  select
                  size="small"
                  fullWidth
                  value={reverify}
                  onChange={(e) => setReverify(e.target.value)}
                  helperText="How often a verified user must scan again."
                >
                  {REVERIFY_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                  <MenuItem value="custom">Custom (days)…</MenuItem>
                </TextField>
              </Grid>
              {reverify === "custom" && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Custom period (days)"
                    type="number"
                    size="small"
                    fullWidth
                    inputProps={{ min: 1, max: 3650 }}
                    value={customReverifyDays}
                    onChange={(e) => setCustomReverifyDays(e.target.value)}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Grace period (days)"
                  type="number"
                  size="small"
                  fullWidth
                  inputProps={{ min: 0, max: 365 }}
                  value={gracePeriodDays}
                  onChange={(e) => setGracePeriodDays(e.target.value)}
                  helperText="Existing accounts (and expired verifications) can dismiss the prompt this long, then it blocks. New signups are always blocked until verified."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Minimum age"
                  type="number"
                  size="small"
                  fullWidth
                  inputProps={{ min: 0, max: 120 }}
                  value={minAge}
                  onChange={(e) => setMinAge(e.target.value)}
                  helperText="Rekognition's estimated age range must clear this; borderline results fall back to the declared birthday."
                />
              </Grid>
            </Grid>

            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Switch
                  checked={retainFaceOnDelete}
                  onChange={(e) => setRetainFaceOnDelete(e.target.checked)}
                />
              }
              label="Keep face template when an account is deleted"
            />
            <Typography variant="caption" color="text.secondary" display="block">
              Off (default): the template is deleted with the account. On: a
              deleted account's face still counts as a duplicate, so
              delete-and-recreate can't bypass the one-account rule. Review with
              counsel before enabling (biometric retention).
            </Typography>

            {mode !== "off" && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Enabled since <strong>{formatDate(enabledAt)}</strong>. Accounts
                created before then are treated as existing users (grace period
                applies).
              </Alert>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper sx={cardSx}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              How it works
            </Typography>
            <Typography variant="body2" color="text.secondary" component="div">
              <ol style={{ paddingLeft: 18, margin: 0 }}>
                <li>The app takes one selfie and sends it to the backend.</li>
                <li>
                  Rekognition estimates an age range. Clearly under the minimum →
                  <em> Under age</em>. Borderline → declared birthday decides
                  (flagged as ambiguous for you).
                </li>
                <li>
                  The face is searched against every verified account. A match
                  on another live account → <em>Duplicate</em>.
                </li>
                <li>
                  Otherwise the face template is indexed under the user's id and
                  the user is <em>Verified</em>. The photo itself is discarded.
                </li>
              </ol>
            </Typography>
            <Typography variant="body2" sx={{ mt: 2 }}>
              <strong>Live-only mode</strong> gates joining rap / debate / karaoke
              live pagents and hosting a stream. Watching is never gated.
            </Typography>
          </Paper>
        </Grid>

        {/* ---------------- Review queue ---------------- */}
        <Grid item xs={12}>
          <Paper sx={cardSx}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>
                Review queue
              </Typography>
              <TextField
                label="Search username / email"
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ minWidth: 240 }}
              />
              <TextField
                select
                size="small"
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{ minWidth: 300 }}
              >
                {STATUS_FILTERS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => setRefreshToken((t) => t + 1)}
                disabled={listLoading}
              >
                Refresh
              </Button>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>User</TableCell>
                    <TableCell sx={headCellSx}>Selfies</TableCell>
                    <TableCell sx={headCellSx}>Submitted from</TableCell>
                    <TableCell sx={headCellSx}>Status</TableCell>
                    <TableCell sx={headCellSx}>Est. age</TableCell>
                    <TableCell sx={headCellSx}>Declared DOB</TableCell>
                    <TableCell sx={headCellSx}>Duplicate of</TableCell>
                    <TableCell sx={headCellSx}>Last attempt</TableCell>
                    <TableCell sx={headCellSx}>Attempts</TableCell>
                    <TableCell sx={headCellSx}>Admin override</TableCell>
                    <TableCell sx={headCellSx} align="right">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          {listLoading ? "Loading…" : "Nothing here."}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {rows.map((row) => {
                    const fv = row.faceVerification || {};
                    const chip = STATUS_CHIP[fv.status] || STATUS_CHIP.unverified;
                    const isRejected =
                      fv.status === "rejected_duplicate" ||
                      fv.status === "rejected_underage" ||
                      fv.status === "needs_review";
                    const appealPending = fv.appealPending === true;
                    const hasAnySelfie = !!(fv.selfieUrl || row.duplicateOf?.selfieUrl);
                    return (
                      <TableRow
                        key={row._id}
                        hover
                        sx={appealPending ? { backgroundColor: "rgba(237, 108, 2, 0.06)" } : undefined}
                      >
                        <TableCell>
                          <Typography sx={{ fontWeight: 600 }}>
                            @{row.username || "—"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.email || ""}
                            {row.isAccountBlocked ? " · banned" : ""}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
                            <SelfieThumb
                              url={fv.selfieUrl}
                              label={`@${row.username || "user"}`}
                              onClick={() => setCompare({ row })}
                            />
                            {row.duplicateOf ? (
                              <>
                                <Typography variant="caption" color="text.secondary">
                                  vs
                                </Typography>
                                <SelfieThumb
                                  url={row.duplicateOf.selfieUrl}
                                  label={`@${row.duplicateOf.username || "matched account"}`}
                                  onClick={() => setCompare({ row })}
                                />
                              </>
                            ) : null}
                          </Box>
                          {hasAnySelfie && (
                            <Button
                              size="small"
                              sx={{ mt: 0.5, px: 0, minWidth: 0, fontSize: 11 }}
                              onClick={() => setCompare({ row })}
                            >
                              {row.duplicateOf ? "Compare" : "Enlarge"}
                            </Button>
                          )}
                        </TableCell>
                        <TableCell sx={{ minWidth: 170 }}>
                          <SubmissionOrigin
                            submission={fv.submission}
                            other={row.duplicateOf?.submission}
                            label={row.duplicateOf ? `@${row.username || "user"}` : null}
                          />
                          {row.duplicateOf && (
                            <Box sx={{ mt: 0.75 }}>
                              <SubmissionOrigin
                                submission={row.duplicateOf.submission}
                                other={fv.submission}
                                label={`@${row.duplicateOf.username || "matched"}`}
                              />
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={chip.label} color={chip.color} />
                          {appealPending && (
                            <Tooltip
                              title={
                                fv.appeal?.note
                                  ? `Appeal note: ${fv.appeal.note}`
                                  : "User requested a review (no note)"
                              }
                            >
                              <Chip
                                size="small"
                                label={`Appeal · ${formatDate(fv.appeal?.requestedAt)}`}
                                color="warning"
                                variant="outlined"
                                sx={{ ml: 0.5, mt: 0.5 }}
                              />
                            </Tooltip>
                          )}
                          {!appealPending && fv.appeal?.resolvedAt && (
                            <Tooltip title={`Appeal resolved (${fv.appeal.resolution || "—"})`}>
                              <Chip
                                size="small"
                                label="appealed"
                                variant="outlined"
                                sx={{ ml: 0.5, mt: 0.5 }}
                              />
                            </Tooltip>
                          )}
                          {fv.ageAmbiguous && (
                            <Tooltip title="Rekognition's age range straddled the minimum; the declared birthday decided.">
                              <Chip
                                size="small"
                                label="age ambiguous"
                                variant="outlined"
                                sx={{ ml: 0.5 }}
                              />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          {fv.ageRangeLow != null && fv.ageRangeHigh != null
                            ? `${fv.ageRangeLow}–${fv.ageRangeHigh}`
                            : "—"}
                        </TableCell>
                        <TableCell>{row.dob || "—"}</TableCell>
                        <TableCell>
                          {row.duplicateOf ? (
                            <>
                              @{row.duplicateOf.username}
                              {row.duplicateOf.isDeleted ? " (deleted)" : ""}
                            </>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell>{formatDate(fv.lastAttemptAt)}</TableCell>
                        <TableCell>{fv.attemptCount ?? 0}</TableCell>
                        <TableCell>
                          {fv.adminOverride?.action ? (
                            <Tooltip title={fv.adminOverride.note || ""}>
                              <span>
                                {fv.adminOverride.action} ·{" "}
                                {formatDate(fv.adminOverride.at)}
                              </span>
                            </Tooltip>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          {isRejected && (
                            <Button
                              size="small"
                              color="success"
                              onClick={() => openAction(row, "approve")}
                              disabled={row.isDeleted}
                            >
                              Approve
                            </Button>
                          )}
                          <Button
                            size="small"
                            onClick={() => openAction(row, "reset")}
                            disabled={row.isDeleted}
                          >
                            Reset
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            onClick={() => openAction(row, "ban")}
                            disabled={row.isDeleted || row.isAccountBlocked}
                          >
                            Ban
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {total > PAGE_SIZE && (
              <Box sx={{ mt: 2 }}>
                <CommonPagination
                  count={total}
                  page={page}
                  rowsPerPage={PAGE_SIZE}
                  onPageChange={(_, newPage) => setPage(newPage)}
                />
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Dialog
        open={!!pendingAction}
        onClose={() => (acting ? null : setPendingAction(null))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {pendingAction?.action === "approve" && "Approve verification"}
          {pendingAction?.action === "reset" && "Reset verification"}
          {pendingAction?.action === "ban" && "Ban account"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {pendingAction?.action === "approve" &&
              `Mark @${pendingAction.user.username} as verified. Use this when a duplicate/underage result was a false positive.`}
            {pendingAction?.action === "reset" &&
              `Clear @${pendingAction.user.username}'s result and delete their face template so they can scan again.`}
            {pendingAction?.action === "ban" &&
              `Block @${pendingAction.user.username} and run the full account-deletion cascade. This is the same as a moderation ban.`}
          </Typography>
          {pendingAction?.user?.faceVerification?.appealPending && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>Appeal pending</strong> since{" "}
              {formatDate(pendingAction.user.faceVerification.appeal?.requestedAt)}.
              {pendingAction.user.faceVerification.appeal?.note
                ? ` User's note: “${pendingAction.user.faceVerification.appeal.note}”`
                : " No note from the user."}{" "}
              This action will close the appeal.
            </Alert>
          )}
          <TextField
            label="Note (optional)"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={actionNote}
            onChange={(e) => setActionNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingAction(null)} disabled={acting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={pendingAction?.action === "ban" ? "error" : "primary"}
            onClick={runAction}
            disabled={acting}
          >
            {acting ? "Working…" : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Side-by-side selfie comparison */}
      <Dialog open={!!compare} onClose={() => setCompare(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          {compare?.row?.duplicateOf
            ? "Rekognition matched these two faces"
            : `Selfie — @${compare?.row?.username || ""}`}
        </DialogTitle>
        <DialogContent>
          {compare?.row && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={compare.row.duplicateOf ? 6 : 12}>
                <SelfiePanel
                  title={`@${compare.row.username || "—"}`}
                  caption={`This account · ${
                    STATUS_CHIP[compare.row.faceVerification?.status]?.label || "—"
                  } · attempt ${formatDate(compare.row.faceVerification?.lastAttemptAt)}`}
                  url={compare.row.faceVerification?.selfieUrl}
                />
                <Box sx={{ mt: 1 }}>
                  <SubmissionOrigin
                    submission={compare.row.faceVerification?.submission}
                    other={compare.row.duplicateOf?.submission}
                  />
                </Box>
              </Grid>
              {compare.row.duplicateOf && (
                <Grid item xs={12} sm={6}>
                  <SelfiePanel
                    title={`@${compare.row.duplicateOf.username || "—"}${
                      compare.row.duplicateOf.isDeleted ? " (deleted)" : ""
                    }`}
                    caption={`Matched account · verified ${formatDate(
                      compare.row.duplicateOf.verifiedAt
                    )}`}
                    url={compare.row.duplicateOf.selfieUrl}
                  />
                  <Box sx={{ mt: 1 }}>
                    <SubmissionOrigin
                      submission={compare.row.duplicateOf.submission}
                      other={compare.row.faceVerification?.submission}
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          )}
          {compare?.row?.duplicateOf && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
              If these are the same person: the new account is a duplicate — leave it rejected
              (the user can sign back into the matched account) or ban it. If they are
              different people: Approve this account.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          {compare?.row && (
            <>
              <Button
                color="success"
                onClick={() => {
                  openAction(compare.row, "approve");
                  setCompare(null);
                }}
                disabled={compare.row.isDeleted}
              >
                Approve
              </Button>
              <Button
                color="error"
                onClick={() => {
                  openAction(compare.row, "ban");
                  setCompare(null);
                }}
                disabled={compare.row.isDeleted || compare.row.isAccountBlocked}
              >
                Ban
              </Button>
            </>
          )}
          <Button onClick={() => setCompare(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
};

export default ManageFaceVerification;
